import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AGENT_STATE_MAP, type AgentState } from "@goyone/shared";
import { api, qs } from "../../lib/api";
import { Icon, type IconName } from "../../components/Icon";
import { Badge, PageHead } from "../../components/ui";
import { OwnerChip } from "../../components/People";
import {
  CardPickerButton,
  applyChoice,
  useCardChoice,
  type CardChoice,
} from "../../components/CardPicker";

/**
 * لوحة إدارة مركز الاتصال.
 *
 * كل رقم فيها من صفوف المكالمات الحقيقية في المدى المختار — لا عدّادات
 * منفصلة تنحرف عن مصدرها. ولوحة الحضور تُستطلع كل عشر ثوانٍ: «من متاح
 * الآن» سؤالُ لحظةٍ لا تقرير.
 */

/**
 * المدى يُتنقَّل فيه لا يُختار من ثلاثة.
 *
 * كانت اللوحةُ تعرض «اليوم» و«آخر سبعة» و«آخر ثلاثين» — فمن أراد شهرًا
 * بعينه أو أسبوعًا مضى لم يجد إليه سبيلًا. صارت كالتقويم: عرضٌ (شهر /
 * أسبوع / يوم) ومرساةٌ يتنقّل بها السهمان، ومدًى مخصَّصٌ بطرفيه لمن أراد
 * ما لا يقع في هذه الوحدات — كربعِ سنةٍ أو موسمٍ.
 */
type DashView = "month" | "week" | "day" | "custom";

const PERIOD_LABELS: Record<
  Exclude<DashView, "custom">,
  { unit: string; now: string }
> = {
  month: { unit: "الشهر", now: "هذا الشهر" },
  week: { unit: "الأسبوع", now: "هذا الأسبوع" },
  day: { unit: "اليوم", now: "اليوم" },
};

const monthFmt = new Intl.DateTimeFormat("ar-EG-u-nu-latn", {
  month: "long",
  year: "numeric",
});
const dayFmt = new Intl.DateTimeFormat("ar-EG-u-nu-latn", {
  weekday: "long",
  day: "numeric",
  month: "long",
});
const shortFmt = new Intl.DateTimeFormat("ar-EG-u-nu-latn", {
  day: "numeric",
  month: "short",
});

/** اليومُ بحدوده — من أوّل ثانيةٍ إلى آخرها. */
function dayStart(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function dayEnd(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}
/** أوّلُ أيام الأسبوع — السبت، كما في تقويم النظام. */
function weekStart(d: Date): Date {
  const x = dayStart(d);
  x.setDate(x.getDate() - ((x.getDay() + 1) % 7));
  return x;
}
/** قيمةُ حقل التاريخ كما يفهمها المتصفّح — بلا انزياح المنطقة. */
function inputDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

interface Stats {
  byStatus: Record<string, number>;
  byDirection: Record<string, number>;
  avgDurationSec: number;
  totalDurationSec: number;
  byAgent: Array<{
    agentId: string;
    status: string;
    count: number;
    durationSec: number;
  }>;
  byDisposition: Array<{
    id: string;
    label: string;
    kind: string;
    count: number;
  }>;
  /* الجودة بقناتيها — تقييم المشرف من مئة ورضا العميل من خمسة. */
  quality: {
    reviews: { count: number; avg: number | null };
    ratings: { sent: number; submitted: number; avg: number | null };
    byAgent: Array<{
      agentId: string;
      reviewCount: number;
      reviewAvg: number | null;
      ratingCount: number;
      ratingAvg: number | null;
    }>;
  };
}

/** بطاقات لوحة الأداء المتاحة للاختيار. */
const TILE_CHOICES: CardChoice[] = [
  { key: "total", label: "إجمالي المكالمات", group: "الأعداد" },
  { key: "out", label: "صادرة", group: "الأعداد" },
  { key: "in", label: "واردة", group: "الأعداد" },
  { key: "done", label: "تمّت", group: "الأعداد" },
  { key: "missed", label: "فائتة / بلا رد", group: "الأعداد" },
  { key: "agents", label: "موظفون نشطون", group: "الأعداد" },
  { key: "avg", label: "متوسط المدة", group: "الوقت والأداء" },
  { key: "totalTime", label: "إجمالي وقت المكالمات", group: "الوقت والأداء" },
  { key: "convRate", label: "نسبة الإتمام", group: "الوقت والأداء" },
  { key: "qa", label: "جودة المشرف", group: "الجودة" },
  { key: "csat", label: "رضا العملاء", group: "الجودة" },
];

/** ما يُعرض قبل أن يختار المستخدم — الستّة التي كانت ثابتة. */
const DEFAULT_TILES = ["total", "out", "in", "done", "missed", "avg"];

export function CallCenterDashboard() {
  const [view, setView] = useState<DashView>("day");
  const [anchor, setAnchor] = useState(() => new Date());
  /* طرفا المدى المخصَّص — نصًّا كما يكتبهما حقل التاريخ. */
  const [customFrom, setCustomFrom] = useState(() => inputDate(new Date()));
  const [customTo, setCustomTo] = useState(() => inputDate(new Date()));

  /* حدود المدى المعروض — منها يُطلب الإحصاء ومنها يُكتب عنوانه. */
  const { from, to } = (() => {
    if (view === "custom") {
      const f = new Date(customFrom || inputDate(new Date()));
      const t = new Date(customTo || customFrom || inputDate(new Date()));
      return { from: dayStart(f), to: dayEnd(t) };
    }
    if (view === "day") return { from: dayStart(anchor), to: dayEnd(anchor) };
    if (view === "week") {
      const f = weekStart(anchor);
      const t = new Date(f);
      t.setDate(t.getDate() + 6);
      return { from: f, to: dayEnd(t) };
    }
    const f = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const t = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    return { from: dayStart(f), to: dayEnd(t) };
  })();

  /* السهمان يتحرّكان بوحدة العرض — شهرًا أو أسبوعًا أو يومًا. */
  const move = (delta: number) => {
    const d = new Date(anchor);
    if (view === "month") d.setMonth(d.getMonth() + delta);
    else if (view === "week") d.setDate(d.getDate() + delta * 7);
    else d.setDate(d.getDate() + delta);
    setAnchor(d);
  };

  /* اسمُ المدى المعروض — لكلِّ عرضٍ صياغتُه، كما في التقويم. */
  const periodLabel =
    view === "custom"
      ? `${shortFmt.format(from)} — ${shortFmt.format(to)}`
      : view === "month"
        ? monthFmt.format(anchor)
        : view === "day"
          ? dayFmt.format(anchor)
          : `${shortFmt.format(from)} — ${shortFmt.format(to)}`;

  const stats = useQuery({
    queryKey: ["cc", "stats", from.toISOString(), to.toISOString()],
    queryFn: () =>
      api<Stats>(
        `/callcenter/stats${qs({ from: from.toISOString(), to: to.toISOString() })}`,
      ),
  });

  const presence = useQuery({
    queryKey: ["cc", "presence"],
    queryFn: () =>
      api<{ items: Array<Record<string, any>> }>("/callcenter/presence"),
    refetchInterval: 10_000,
  });

  const { chosen, setChosen, reset } = useCardChoice(
    "goyone.cards.callDashboard",
    DEFAULT_TILES,
  );

  const s = stats.data;
  const total = Object.values(s?.byStatus ?? {}).reduce((a, b) => a + b, 0);
  const completed = s?.byStatus?.COMPLETED ?? 0;
  const missed = (s?.byStatus?.MISSED ?? 0) + (s?.byStatus?.NO_ANSWER ?? 0);

  /* تجميع صفوف groupBy (موظف×حالة) إلى صفٍّ لكل موظف. */
  const agents = new Map<
    string,
    { total: number; completed: number; missed: number; durationSec: number }
  >();
  for (const r of s?.byAgent ?? []) {
    const row = agents.get(r.agentId) ?? {
      total: 0,
      completed: 0,
      missed: 0,
      durationSec: 0,
    };
    row.total += r.count;
    if (r.status === "COMPLETED") row.completed += r.count;
    if (r.status === "MISSED" || r.status === "NO_ANSWER")
      row.missed += r.count;
    row.durationSec += r.durationSec;
    agents.set(r.agentId, row);
  }

  return (
    <div className="page">
      <PageHead
        title="لوحة مركز الاتصال"
        subtitle={`الأرقام من المكالمات المسجَّلة في ${periodLabel} — والحضور لحظيّ`}
        actions={
          <span
            className="row"
            style={{ gap: "var(--sp-2)", flexWrap: "wrap" }}
          >
            {/* التنقّل بالسهمين، والزرّ بينهما يسمّي المدى ويعيد إلى الآن. */}
            {view !== "custom" && (
              <span className="row" style={{ gap: 2 }}>
                <button
                  className="btn btn-icon"
                  onClick={() => move(1)}
                  aria-label={`${PERIOD_LABELS[view].unit} التالي`}
                  title={`${PERIOD_LABELS[view].unit} التالي`}
                >
                  <Icon name="chevronLeft" size={15} />
                </button>
                <button
                  className="btn btn-sm"
                  onClick={() => setAnchor(new Date())}
                  title={`العودة إلى ${PERIOD_LABELS[view].now}`}
                >
                  {periodLabel}
                </button>
                <button
                  className="btn btn-icon"
                  onClick={() => move(-1)}
                  aria-label={`${PERIOD_LABELS[view].unit} السابق`}
                  title={`${PERIOD_LABELS[view].unit} السابق`}
                >
                  <Icon name="chevronRight" size={15} />
                </button>
              </span>
            )}

            {/* المدى المخصَّص: طرفاه حقلا تاريخ — ولا سهمَ له. */}
            {view === "custom" && (
              <span className="row" style={{ gap: 4 }}>
                <input
                  className="input"
                  type="date"
                  value={customFrom}
                  max={customTo}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  aria-label="من تاريخ"
                />
                <span className="tiny mute">إلى</span>
                <input
                  className="input"
                  type="date"
                  value={customTo}
                  min={customFrom}
                  onChange={(e) => setCustomTo(e.target.value)}
                  aria-label="إلى تاريخ"
                />
              </span>
            )}

            <span className="row" style={{ gap: 2 }}>
              {(
                [
                  ["month", "شهر"],
                  ["week", "أسبوع"],
                  ["day", "يوم"],
                  ["custom", "مخصَّص"],
                ] as Array<[DashView, string]>
              ).map(([k, label]) => (
                <button
                  key={k}
                  className={`btn btn-sm${view === k ? " btn-primary" : ""}`}
                  onClick={() => {
                    /* الانتقالُ إلى المخصَّص يبدأ من المدى المعروض لا من فراغ. */
                    if (k === "custom") {
                      setCustomFrom(inputDate(from));
                      setCustomTo(inputDate(to));
                    }
                    setView(k);
                  }}
                >
                  {label}
                </button>
              ))}
            </span>
            <CardPickerButton
              choices={TILE_CHOICES}
              chosen={chosen}
              onChange={setChosen}
              onReset={reset}
            />
          </span>
        }
      />

      {/* ---------------- الأرقام الكلية — تُختار كبطاقات القوائم ---------------- */}
      <div className="insights">
        {applyChoice(
          [
            {
              key: "total",
              icon: "phone" as const,
              color: "#3672EA",
              label: "إجمالي المكالمات",
              value: total,
            },
            {
              key: "out",
              icon: "send" as const,
              color: "#1D46A6",
              label: "صادرة",
              value: s?.byDirection?.OUTBOUND ?? 0,
            },
            {
              key: "in",
              icon: "inbox" as const,
              color: "#5596FF",
              label: "واردة",
              value: s?.byDirection?.INBOUND ?? 0,
            },
            {
              key: "done",
              icon: "check" as const,
              color: "#51A521",
              label: "تمّت",
              value: completed,
            },
            {
              key: "missed",
              icon: "alert" as const,
              color: "#DC2626",
              label: "فائتة / بلا رد",
              value: missed,
            },
            {
              key: "avg",
              icon: "clock" as const,
              color: "#BF930B",
              label: "متوسط المدة",
              value: s ? formatDuration(s.avgDurationSec) : "—",
            },
            {
              key: "totalTime",
              icon: "chartBar" as const,
              color: "#8791A1",
              label: "إجمالي وقت المكالمات",
              value: s ? formatDuration(s.totalDurationSec) : "—",
            },
            {
              key: "agents",
              icon: "users" as const,
              color: "#1D46A6",
              label: "موظفون نشطون",
              value: agents.size,
            },
            {
              key: "convRate",
              icon: "funnel" as const,
              color: "#51A521",
              label: "نسبة الإتمام",
              value:
                total > 0 ? `${Math.round((completed / total) * 100)}٪` : "0٪",
            },
            {
              key: "qa",
              icon: "check" as const,
              color: "#51A521",
              label: "جودة المشرف",
              value:
                s?.quality?.reviews.avg != null
                  ? `${s.quality.reviews.avg}/100`
                  : "—",
            },
            {
              key: "csat",
              icon: "star" as const,
              color: "#BF930B",
              label: "رضا العملاء",
              value:
                s?.quality?.ratings.avg != null
                  ? `${s.quality.ratings.avg}/5`
                  : "—",
            },
          ],
          chosen,
        ).map((t) => (
          <Tile
            key={t.key}
            icon={t.icon}
            color={t.color}
            label={t.label}
            value={t.value}
          />
        ))}
      </div>

      <div className="two-col">
        <div className="col" style={{ gap: "var(--sp-4)" }}>
          {/* ---------------- أداء الموظفين ---------------- */}
          <div className="card">
            <div className="card-head">
              <h2 className="card-title">أداء الموظفين</h2>
            </div>
            {agents.size === 0 ? (
              <div className="card-body">
                <p className="mute center">لا مكالمات في هذا المدى.</p>
              </div>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>الموظف</th>
                      <th>المكالمات</th>
                      <th>تمّت</th>
                      <th>بلا رد</th>
                      <th>إجمالي الوقت</th>
                      <th>نسبة الإتمام</th>
                      <th title="متوسط تقييم المشرف من مئة">الجودة</th>
                      <th title="متوسط تقييم العملاء من خمسة">رضا العملاء</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...agents.entries()]
                      .sort((a, b) => b[1].total - a[1].total)
                      .map(([agentId, row]) => {
                        const q = s?.quality?.byAgent.find(
                          (x) => x.agentId === agentId,
                        );
                        return (
                          <tr key={agentId}>
                            <td>
                              <OwnerChip ownerId={agentId} size={24} />
                            </td>
                            <td className="num">{row.total}</td>
                            <td className="num">{row.completed}</td>
                            <td className="num">{row.missed}</td>
                            <td className="num">
                              {formatDuration(row.durationSec)}
                            </td>
                            <td className="num">
                              {row.total > 0
                                ? Math.round((row.completed / row.total) * 100)
                                : 0}
                              ٪
                            </td>
                            {/* «—» تعني لا تقييمات في المدى — لا صفرًا يظلم. */}
                            <td className="num">
                              {q?.reviewAvg != null ? q.reviewAvg : "—"}
                            </td>
                            <td className="num">
                              {q?.ratingAvg != null ? `${q.ratingAvg} ★` : "—"}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ---------------- النتائج ---------------- */}
          <div className="card">
            <div className="card-head">
              <h2 className="card-title">بمَ تنتهي المكالمات</h2>
            </div>
            <div className="card-body">
              {(s?.byDisposition ?? []).length === 0 && (
                <p className="mute center">لا نتائج مسجَّلة في المدى.</p>
              )}
              <div className="col" style={{ gap: "var(--sp-2)" }}>
                {(s?.byDisposition ?? [])
                  .sort((a, b) => b.count - a.count)
                  .map((d) => (
                    <div
                      key={d.id}
                      className="row"
                      style={{ gap: "var(--sp-2)" }}
                    >
                      <Badge kind={d.kind as any}>{d.label}</Badge>
                      <span
                        className="cc-bar"
                        style={{
                          width: `${Math.round((d.count / Math.max(1, completed)) * 100)}%`,
                        }}
                        aria-hidden="true"
                      />
                      <span
                        className="num tiny"
                        style={{ marginInlineStart: "auto" }}
                      >
                        {d.count}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>

        {/* ---------------- الحضور اللحظي ---------------- */}
        <div className="card">
          <div className="card-head">
            <h2 className="card-title">الفريق الآن</h2>
          </div>
          <div className="card-body">
            {(presence.data?.items ?? []).length === 0 && (
              <p className="mute center">
                لا أحد فتح الكونسول بعد — الحالة تظهر أول ما يغيّرها موظف.
              </p>
            )}
            <div className="col" style={{ gap: "var(--sp-3)" }}>
              {(presence.data?.items ?? []).map((p) => {
                const def = AGENT_STATE_MAP[p.state as AgentState];
                return (
                  <div
                    key={p.userId}
                    className="row"
                    style={{ gap: "var(--sp-2)" }}
                  >
                    <OwnerChip ownerId={p.userId} size={26} />
                    <Badge kind={def?.kind ?? "neutral"}>
                      {def?.label ?? p.state}
                    </Badge>
                    {p.companyPhone && (
                      <span
                        className="tiny mute num"
                        dir="ltr"
                        title="خط الشركة"
                      >
                        {p.companyPhone}
                      </span>
                    )}
                    {/* أعلام الجلسة يبلّغها متصفح الموظف: المشرف يرى من يسجّل ومن يُراقَب. */}
                    {p.recordingOn && (
                      <span
                        className="cc-flag rec"
                        title="جلسة تسجيل المكالمات مفتوحة"
                      >
                        <span className="cc-rec-dot" /> تسجيل
                      </span>
                    )}
                    {p.monitorOn && (
                      <span
                        className="cc-flag mon"
                        title="مراقبة الشاشة فعّالة"
                      >
                        <Icon name="video" size={12} /> مراقبة
                      </span>
                    )}
                    {p.currentCall && (
                      <span className="tiny mute num" dir="ltr">
                        {p.currentCall.phone}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Tile({
  icon,
  color,
  label,
  value,
}: {
  icon: IconName;
  color: string;
  label: string;
  value: number | string;
}) {
  return (
    <div
      className="insight insight-static"
      style={{ ["--insight-color" as string]: color }}
    >
      <span className="insight-icon">
        <Icon name={icon} size={17} />
      </span>
      <span className="insight-body">
        <span className="insight-label">{label}</span>
        <span className="insight-value num">{value}</span>
      </span>
    </div>
  );
}

function formatDuration(sec: number): string {
  if (sec < 60) return `${sec}ث`;
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return h > 0 ? `${h}س ${m}د` : `${m}د`;
}
